/**
 * Global layer manager for tracking overlay component stacking order.
 * Uses window-level state to support cross-bundle communication (e.g., MFEs).
 *
 * When multiple Dialog/Drawer components are open, only the topmost layer
 * should respond to outside click events. This manager tracks the z-order
 * to enable that behavior.
 */

const WINDOW_PROPERTY_LAYER_STACK = "__ANVIL_LAYER_MANAGER__" as const;

// Declare global window property (matches DndSort pattern)
declare global {
  interface Window {
    [WINDOW_PROPERTY_LAYER_STACK]?: string[];
  }
}

/**
 * Get or initialize the global layer stack on the window object.
 * This ensures all instances of the design system share the same layer stack,
 * even across different bundles (e.g., base app + MFEs).
 *
 * @returns The global layers array
 */
const getGlobalLayers = (): string[] => {
  // SSR safety check (matches usePrefersColorScheme pattern)
  if (typeof window === "undefined") {
    return [];
  }

  if (!window[WINDOW_PROPERTY_LAYER_STACK]) {
    window[WINDOW_PROPERTY_LAYER_STACK] = [];
  }

  return window[WINDOW_PROPERTY_LAYER_STACK];
};

/**
 * Register a new layer in the stack.
 * If the layer is already registered, this is a no-op.
 *
 * @param id - Unique identifier for the layer
 */
export const registerLayer = (id: string): void => {
  const layers = getGlobalLayers();
  if (!layers.includes(id)) {
    layers.push(id);
  }
};

/**
 * Unregister a layer from the stack.
 * If the layer is not found, this is a no-op.
 *
 * @param id - Unique identifier for the layer
 */
export const unregisterLayer = (id: string): void => {
  const layers = getGlobalLayers();
  const index = layers.indexOf(id);
  if (index !== -1) {
    layers.splice(index, 1);
  }
};

/**
 * Check if a layer is the topmost layer in the stack.
 *
 * @param id - Unique identifier for the layer
 * @returns True if the layer is on top, false otherwise
 */
export const isTopLayer = (id: string): boolean => {
  const layers = getGlobalLayers();
  return layers[layers.length - 1] === id;
};

/**
 * Get all registered layers in order (bottom to top).
 * Returns a readonly copy for debugging purposes.
 *
 * @returns Readonly array of layer IDs
 */
export const getLayers = (): readonly string[] => {
  const layers = getGlobalLayers();
  return [...layers];
};
